<template>
  <div class="message-thread">
    <div class="thread-head">
      <div class="thread-subject">{{ conversation.subject }}</div>
      <div class="thread-participants">
        <div v-if="conversation.outgoing">An: {{ conversation.otherParticipant }}</div>
        <template v-else>
          <div>Von: {{ conversation.otherParticipant }}</div>
          <div v-if="conversation.ownParticipant">
            An: {{ conversation.ownParticipant }}
          </div>
        </template>
      </div>
    </div>
    <ConversationView
      v-if="requestConversation"
      :conversation="conversation"
      :withHeader="false"
      @answered="$emit('answered')"
    />
    <div v-else class="thread-messages">
      <div
        v-for="message in sortedMessages"
        :key="message.id"
        class="thread-message"
        :class="{ own: message.own }"
      >
        <div class="message-meta">
          {{ message.author }} &middot; {{ formatDateTime(message.timestamp) }}
        </div>
        <div class="message-body">{{ message.message }}</div>
      </div>
    </div>
    <div v-if="conversation.readers && conversation.readers.length > 0" class="thread-readers">
      Gelesen von:
      <span v-for="reader in conversation.readers" :key="reader.userName" class="reader">
        {{ reader.userName }} ({{ formatDateTime(reader.readTimestamp) }})
      </span>
    </div>
    <div v-if="!requestConversation" class="thread-reply">
      <Textarea
        v-model="replyText"
        :autoResize="false"
        :maxlength="maxMessageLength"
        placeholder="Antwort schreiben"
        style="width: 100%; height: 120px"
      />
      <div class="reply-actions">
        <span class="reply-counter">{{ replyText.length }} / {{ maxMessageLength }}</span>
        <Button
          label="Antwort senden"
          icon="pi pi-send"
          :disabled="!replyAllowed"
          @click="sendReply()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import { reportFailure } from "../../services/ServerConnection/FailureToast";
import ConversationView from "../conversation/ConversationView.vue";
import Button from "primevue/button";
import Textarea from "primevue/textarea";

const MAX_MESSAGE_LENGTH = 1000;

export default {
  name: "MessageThread",
  components: {
    Button,
    ConversationView,
    Textarea,
  },
  emits: ["replied", "answered"],
  props: {
    conversation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      replyText: "",
      sending: false,
      maxMessageLength: MAX_MESSAGE_LENGTH,
    };
  },
  computed: {
    requestConversation() {
      return (
        this.conversation.headerMessage && this.conversation.headerMessage.length > 0
      );
    },
    sortedMessages() {
      var messages = this.conversation.messages ? this.conversation.messages.slice(0) : [];
      return messages.sort((m1, m2) => (m1.timestamp > m2.timestamp ? 1 : -1));
    },
    replyAllowed() {
      return !this.sending && this.replyText.trim().length > 0;
    },
  },
  methods: {
    sendReply() {
      if (!this.replyAllowed) {
        return;
      }
      this.sending = true;
      ServerConnection.post("messages/" + this.conversation.id + "/reply", {
        message: this.replyText.trim(),
      })
        .then(() => {
          this.replyText = "";
          this.sending = false;
          this.$emit("replied");
        })
        .catch((error) => {
          this.sending = false;
          reportFailure(this, error, "Antwort konnte nicht gesendet werden");
        });
    },
    formatDateTime(date) {
      if (date && date.length > 0) {
        var pattern = /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/;
        return date.replace(pattern, "$3.$2.$1 $4:$5 Uhr");
      }
      return "";
    },
  },
};
</script>

<style scoped>
.message-thread {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.thread-subject {
  font-size: 18px;
  font-weight: 600;
}
.thread-participants {
  color: #555;
  font-size: 14px;
}
.thread-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.thread-message {
  border: 1px solid #e4e4e4;
  border-radius: 6px;
  padding: 8px 10px;
  background: #fbfbfb;
}
.thread-message.own {
  background: #eef6ff;
}
.message-meta {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}
.message-body {
  white-space: pre-wrap;
  word-break: break-word;
}
.thread-readers {
  font-size: 13px;
  color: #555;
}
.reader {
  margin-right: 8px;
}
.reply-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}
.reply-counter {
  font-size: 12px;
  color: #777;
}
</style>
